import { type ClimateRiskAnalysisProviderEnum } from 'pages/ClimateRisk/enum';

export const AggregationLevel = {
   AUTOMATIC: 'AUTOMATIC',
   YEAR: 'YEAR',
   HALF_YEAR: 'HALF_YEAR',
   QUARTER: 'QUARTER',
   MONTH: 'MONTH',
   YEAR_TO_DATE: 'YEAR_TO_DATE',
} as const;
export type AggregationLevelEnum = (typeof AggregationLevel)[keyof typeof AggregationLevel];

export type ClimateRiskAnalysisProvider = (typeof ClimateRiskAnalysisProviderEnum)[keyof typeof ClimateRiskAnalysisProviderEnum];

export const AuditAction = {
   review: 'review',
   unreview: 'unreview',
   approve: 'approve',
   unapprove: 'unapprove',
   reject: 'reject',
   archive: 'archive',
   unarchive: 'unarchive',
} as const;

export const AuditActions = ['REVIEW', 'APPROVE', 'ARCHIVE', 'UNREVIEW', 'UNAPPROVE', 'REJECT', 'UNARCHIVE'] as const;

export const ApprovalStatus = {
   NO_VERIFICATION_NEEDED: 'NO_VERIFICATION_NEEDED',
   IN_PROGRESS: 'IN_PROGRESS',
   REVIEWED: 'REVIEWED',
   APPROVED: 'APPROVED',
   ARCHIVED: 'ARCHIVED',
} as const;
export type ApprovalStatusEnum = (typeof ApprovalStatus)[keyof typeof ApprovalStatus];

export const AuditRoles = {
   AUDITOR: 'AUDITOR',
   EDITOR: 'EDITOR',
} as const;
export type AuditRoleEnum = (typeof AuditRoles)[keyof typeof AuditRoles];

/** User, group and rights events a tenant administrator can export from the audit log. */
export const AuditLogEvent = {
   USER_CREATED: 'USER_CREATED',
   USER_UPDATED: 'USER_UPDATED',
   USER_DELETED: 'USER_DELETED',
   USER_STATUS_CHANGED: 'USER_STATUS_CHANGED',
   GROUP_CREATED: 'GROUP_CREATED',
   GROUP_DELETED: 'GROUP_DELETED',
   GROUP_MEMBER_ADDED: 'GROUP_MEMBER_ADDED',
   GROUP_MEMBER_REMOVED: 'GROUP_MEMBER_REMOVED',
   GROUP_RIGHT_ADDED: 'GROUP_RIGHT_ADDED',
   GROUP_RIGHT_REMOVED: 'GROUP_RIGHT_REMOVED',
} as const;
export type AuditLogEventEnum = (typeof AuditLogEvent)[keyof typeof AuditLogEvent];

export const AuditEntityFunction = {
   review: 'reviewEntity',
   unreview: 'unreviewEntity',
   reject: 'rejectEntity',
   approve: 'approveEntity',
   unapprove: 'unapproveEntity',
   archive: 'archiveEntity',
   unarchive: 'unarchiveEntity',
} as const;

export const CalculationFieldType = {
   FUEL_CONSUMPTION_COAL_COAL_PRODUCTS: 'FUEL_CONSUMPTION_COAL_COAL_PRODUCTS',
   FUEL_CONSUMTPION_CRUED_OIL_PETROLEUM_PRODUCTS: 'FUEL_CONSUMTPION_CRUED_OIL_PETROLEUM_PRODUCTS',
   FUEL_CONSUMTPION_NATURAL_GAS: 'FUEL_CONSUMTPION_NATURAL_GAS',
   FUEL_CONSUMTPION_OTHER_FOSSIL_SOURCES: 'FUEL_CONSUMTPION_OTHER_FOSSIL_SOURCES',
   FUEL_CONSUMPTION_RENEWABLE_SOURCES: 'FUEL_CONSUMPTION_RENEWABLE_SOURCES',
   CONSUMPTION_ELECTRICITY_HEAT_STEAM_COOLING_FOSSIL_SOURCES: 'CONSUMPTION_ELECTRICITY_HEAT_STEAM_COOLING_FOSSIL_SOURCES',
   CONSUMPTION_ELECTRICITY_HEAT_STEAM_COOLING_RENEWABLE_SOURCES: 'CONSUMPTION_ELECTRICITY_HEAT_STEAM_COOLING_RENEWABLE_SOURCES',
   CONSUMPTION_NUCLEAR_PRODUCTS: 'CONSUMPTION_NUCLEAR_PRODUCTS',
   CONSUMPTION_SELF_GENERATED_NON_FUEL_RENEWABLE_ENERGY: 'CONSUMPTION_SELF_GENERATED_NON_FUEL_RENEWABLE_ENERGY',
   ENERGY_PRODUCTION_RENEWABLE: 'ENERGY_PRODUCTION_RENEWABLE',
   ENERGY_PRODUCTION_NON_RENEWABLE: 'ENERGY_PRODUCTION_NON_RENEWABLE',
} as const;

export type CalculationFieldTypeEnum = (typeof CalculationFieldType)[keyof typeof CalculationFieldType];

export const CommonActionType = {
   UPDATE: 'UPDATE',
   DELETE: 'DELETE',
   APPROVE: 'APPROVE',
   REVIEW: 'REVIEW',
   UNREVIEW: 'UNREVIEW',
   UNAPPROVE: 'UNAPPROVE',
   UNARCHIVE: 'UNARCHIVE',
} as const;

export type CommonActionTypeEnum = (typeof CommonActionType)[keyof typeof CommonActionType];

export const CurrencyRateTypes = {
   CLOSING_RATE: 'CLOSING_RATE',
   AVERAGE_RATE: 'AVERAGE_RATE',
} as const;

export type CurrencyRateTypeEnum = (typeof CurrencyRateTypes)[keyof typeof CurrencyRateTypes];

export const ChartType = {
   SCATTER_CHART: 'SCATTER_CHART',
   AREA_CHART: 'AREA_CHART',
   BAR_CHART: 'BAR_CHART',
   FUNNEL_CHART: 'FUNNEL_CHART',
   LINE_CHART: 'LINE_CHART',
   PIE_CHART: 'PIE_CHART',
   RADIAL_BAR_CHART: 'RADIAL_BAR_CHART',
   RADAR_CHART: 'RADAR_CHART',
   COMPOSED_CHART: 'COMPOSED_CHART',
   TABLE_UI: 'TABLE_UI',
   BAR: 'bar',
   BUBBLE: 'bubble',
   DOUGHNUT: 'doughnut',
   LINE: 'line',
   PIE: 'pie',
   POLAR_AREA: 'polarArea',
   RADAR: 'radar',
   SCATTER: 'scatter',
} as const;

export type ChartTypeEnum = (typeof ChartType)[keyof typeof ChartType];

export const ChartCategory: Record<string, readonly (ChartTypeEnum | CustomDiagrammTypeEnum)[]> = {
   BAR: [ChartType.BAR_CHART, ChartType.BAR, ChartType.RADIAL_BAR_CHART],
   LINE: [ChartType.LINE_CHART, ChartType.LINE, ChartType.AREA_CHART],
   PIE: [ChartType.PIE_CHART, ChartType.PIE, ChartType.DOUGHNUT],
   SCATTER: [ChartType.SCATTER_CHART, ChartType.SCATTER],
} as const;

export const ChartComparisonLevel = {
   TIME: 'TIME',
   ORGANIZATION: 'ORGANIZATION',
   TAG: 'TAG',
} as const;
export type ChartComparisonType = (typeof ChartComparisonLevel)[keyof typeof ChartComparisonLevel];

export const CustomDiagrammType = {
   NUMBER: 'NUMBER',
   PERCENT: 'PERCENT',
   PERCENT_MANY: 'PERCENT_MANY',
   TABLE: 'TABLE',
} as const;

export type CustomDiagrammTypeEnum = (typeof CustomDiagrammType)[keyof typeof CustomDiagrammType];

export const DiagrammType = {
   ...ChartType,
   ...CustomDiagrammType,
} as const;

export const EmissionWeightUnits = {
   kg: 'kg',
   t: 't',
   lb: 'lb',
   Mt: 'Mt',
   Gt: 'Gt',
} as const;
export type EmissionWeightUnitEnum = (typeof EmissionWeightUnits)[keyof typeof EmissionWeightUnits];
export type EmissionUnitEnum = `${EmissionWeightUnitEnum} CO₂`;

// API: organizationRelationTypeEnum
export const EntityRelationType = {
   CUSTOMER: 'CUSTOMER',
   SUPPLIER: 'SUPPLIER',
   SUBSIDIARY: 'SUBSIDIARY',
} as const;

export const ESRSFinancialEffectType = {
   FINANCIAL_POSITION: 'FINANCIAL_POSITION',
   FINANCIAL_PERFORMANCE: 'FINANCIAL_PERFORMANCE',
   CASH_FLOWS: 'CASH_FLOWS',
   ACCESS_TO_FINANCE: 'ACCESS_TO_FINANCE',
   COST_OF_CAPTIAL: 'COST_OF_CAPITAL',
} as const;
export type ESRSFinancialEffectTypeEnum = (typeof ESRSFinancialEffectType)[keyof typeof ESRSFinancialEffectType];

export const ESRSMaterialityScaleType = {
   SCALE: 'SCALE',
   SCOPE: 'SCOPE',
   REMEDIABILITY: 'REMEDIABILITY',
   IMPACT_LIKELIHOOD: 'IMPACT_LIKELIHOOD',
   FINANCIAL_LIKELIHOOD: 'FINANCIAL_LIKELIHOOD',
   SIZE_OF_FINANCIAL_EFFECT: 'SIZE_OF_FINANCIAL_EFFECT',
   SEVERITY_SCORE: 'SEVERITY_SCORE',
   IMPACT_SCORE: 'IMPACT_SCORE',
   FINANCIAL_SCORE: 'FINANCIAL_SCORE',
   CRITICAL_IMPACT: 'CRITICAL_IMPACT',
} as const;
export type ESRSMaterialityScaleTypeEnum = (typeof ESRSMaterialityScaleType)[keyof typeof ESRSMaterialityScaleType];
export const ESRSMaterialityImpactType = {
   ACTUAL: 'ACTUAL',
   POTENTIAL: 'POTENTIAL',
} as const;
export type ESRSMaterialityImpactTypeEnum = (typeof ESRSMaterialityImpactType)[keyof typeof ESRSMaterialityImpactType];

export const ESRSMaterialityImpactValenceType = {
   POSITIVE: 'POSITIVE',
   NEGATIVE: 'NEGATIVE',
} as const;

export const ESRSMaterialityFinancialValenceType = {
   RISK: 'RISK',
   OPPORTUNITY: 'OPPORTUNITY',
} as const;

export const ESRSMaterialityType = {
   IMPACT: 'IMPACT',
   FINANCIAL: 'FINANCIAL',
} as const;
export type ESRSMaterialityTypeEnum = (typeof ESRSMaterialityType)[keyof typeof ESRSMaterialityType];

export const ESRSRiskType = {
   PHYSICAL: 'PHYSICAL',
   TRANSITION: 'TRANSITION',
} as const;
export type ESRSRiskTypeEnum = (typeof ESRSRiskType)[keyof typeof ESRSRiskType];

export const ESRSTimeHorizon = {
   SHORT_TERM: 'SHORT_TERM',
   MID_TERM: 'MID_TERM',
   LONG_TERM: 'LONG_TERM',
} as const;
export type ESRSTimeHorizonEnum = (typeof ESRSTimeHorizon)[keyof typeof ESRSTimeHorizon];

export const ESRSValueChainPosition = {
   UPSTREAM: 'UPSTREAM',
   DOWNSTREAM: 'DOWNSTREAM',
   OWN_OPERATIONS: 'OWN_OPERATIONS',
} as const;
export type ESRSValueChainPositionEnum = (typeof ESRSValueChainPosition)[keyof typeof ESRSValueChainPosition];

export const EUOfficalLangEnum = [
   'bg',
   'es',
   'cs',
   'da',
   'de',
   'et',
   'el',
   'en',
   'fr',
   'ga',
   'hr',
   'it',
   'lv',
   'lt',
   'hu',
   'mt',
   'nl',
   'pl',
   'pt',
   'ro',
   'sk',
   'sl',
   'fi',
   'sv',
] as const;

// Financial Statements Line Item
export const FSLIType = {
   REVENUE: 'REV',
   CAPITAL_EXPENDITURE: 'CAPEX',
   OPERATIONAL_EXPENDITURE: 'OPEX',
} as const;
export type FSLITypeEnum = (typeof FSLIType)[keyof typeof FSLIType];

export const FootprintType = {
   ORGANISATION: 'ORGANISATION',
   PRODUCT: 'PRODUCT',
} as const;

export type FootprintTypeEnum = (typeof FootprintType)[keyof typeof FootprintType];

export const GlossaryKindEnum = ['ABBREVIATION', 'DEFINITION'] as const;

export const GoalReferenceKind = {
   KPI_AREA: 'kpiArea',
   KPI: 'kpi',
   KPI_SUB: 'kpiSub',
   KPI_FIELD: 'kpiField',
   BUSINESS_ACTIVITY: 'businessactivity',
   REPORT: 'report',
   FOOTPRINT: 'footprint',
} as const;

export const ImportJobTypes = {
   SUCCESS: 'SUCCESS',
   ERROR: 'ERROR',
   WARNING: 'WARNING',
   PENDING: 'PENDING',
   DELETED: 'DELETED',
} as const;

export type ImportJobType = (typeof ImportJobTypes)[keyof typeof ImportJobTypes];

export const ImportLogMessageTypes = {
   DEBUG: 'DEBUG',
   LOG: 'LOG',
   INFO: 'INFO',
   ERROR: 'ERROR',
   WARNING: 'WARNING',
} as const;

export type ImportLogMessageType = (typeof ImportLogMessageTypes)[keyof typeof ImportLogMessageTypes];

export const InstanceKind = ['TEMPLATE', 'INSTANCE'] as const;

export { KPIFieldType, type KPIFieldTypeEnum } from './kpiFieldTypes';

export const KPIFieldLinkType = {
   LINK: 'LINK',
   SUGGESTION: 'SUGGESTION',
} as const;

export const KPIFieldConsolidationMethods = ['PRO-RATIO', 'FULL'] as const;

export const KPIValueType = {
   DECIMAL: 'DECIMAL',
   TEXT: 'TEXT',
   DATE: 'DATE',
   BOOLEAN: 'BOOLEAN',
   INTEGER: 'INTEGER',
} as const;

export type KPIFieldConsolidationMethodEnum = (typeof KPIFieldConsolidationMethods)[number];

export type KPIValueTypeEnum = (typeof KPIValueType)[keyof typeof KPIValueType];

export const KPIValueItemTypes = ['REQUIRED', 'FORBIDDEN'] as const;

export const KPIValueUnits = [
   '%',
   'l',
   'ml',
   'kl',
   'cm³',
   'm³',
   'yd³', // cubic yard
   'fl oz',
   'gal',
   'galimp',
   'cft',
   'ccf',
   'mcf',
   'µg',
   'g',
   'kg',
   't',
   'lb',
   'oz',
   'st', // stone xD 1 st. = 14 Pfd
   'tn', // short ton
   'sqft', // square foot
   'scf', // standard cubic foot

   'Wh',
   'kWh',
   'kWh/t',
   'MWh',
   'therm-US',
   'BTU',
   'MMBTU',
   'GWh',
   'J',
   'TJ',
   'MJ',
   'GJ',
   'kWp', // kilowatt peak

   'cm²',
   'dm²',
   'm²',
   'a',
   'ha',
   'km²',

   'mm',
   'cm',
   'dm',
   'km',
   'in',
   'ft',
   'yd',
   'mi',
   'mil',
   'nmi', // nautical mile

   'b',
   'B',
   'Kb',
   'KB',
   'Mb',
   'MB',
   'Gb',
   'GB',
   'Tb',
   'TB',
   'PB',

   'srm',
   'rm',

   'g/km',
   'kg/km',
   't/km',
   'CO₂/t',
   'CO₂/kg',
   'person-night',
   'CPU-hour',
   'TB-hour',
   'GB-hour',

   'celsius',
   'degree',
   'fahrenheit',

   'ns',
   'µs',
   'ms',
   's',
   'm',
   'h',
   'day',
   'week',
   'month',
   'year',

   'kg CO₂',
   't CO₂',
   'lb CO₂',
   'Mt CO₂',
   'Gt CO₂',
] as const;

export type KPIValueUnitEnum = (typeof KPIValueUnits)[number];

export const LoginOptions = ['PASSWORD', 'GOOGLE', 'AZURE', 'SLACK', 'GITHUB', 'OPENID', 'OKTA', 'WEBAUTHN', 'SAML'] as const;
export type LoginOptionEnum = (typeof LoginOptions)[number];

export const LogKind = {
   EMAIL: 'EMAIL',
   CREATE: 'CREATE',
   UPDATE: 'UPDATE',
   DELETE: 'DELETE',
   USER_ACTION: 'USER_ACTION',
} as const;

export type LogKindType = (typeof LogKind)[keyof typeof LogKind];

export const LogLevelEnum = ['LOG', 'INFO', 'WARNING', 'ERROR'] as const;

export type LogLevel = (typeof LogLevelEnum)[number];

export const NextAuditStatus = {
   review: 'REVIEWED',
   unreview: 'IN_PROGRESS',
   approve: 'APPROVED',
   reject: 'IN_PROGRESS',
   unapprove: 'IN_PROGRESS',
   archive: 'ARCHIVED',
   unarchive: 'APPROVED',
} as const;

export const OfficialEULanguagues = {
   BULGARIAN: 'BG',
   ESPANOL: 'ES',
   CZECH: 'CS',
   DANISH: 'DA',
   GERMAN: 'DE',
   ESTONIAN: 'ET',
   GREEK: 'EL',
   ENGLISH: 'EN',
   FRENCH: 'FR',
   IRISH: 'GA',
   CROATIAN: 'HR',
   ITALIAN: 'IT',
   LATVIAN: 'LV',
   LITHUANIAN: 'LT',
   HUNGARIAN: 'HU',
   MALTESE: 'MT',
   DUTCH_FLEMISH: 'NL',
   POLISH: 'PL',
   PORTUGUESE: 'PT',
   ROMANIAN_MOLDAVIAN_MOLDOVAN: 'RO',
   SLOVAK: 'SK',
   SLOVENIAN: 'SL',
   FINNISH: 'FI',
   SWEDISH: 'SV',
} as const;

export const OrganisationType = {
   REGION: 'REGION',
   COMMUNE: 'COMMUNE',
   ORGANIZATION: 'ORGANIZATION',
   AREA: 'AREA',
   PRODUCT: 'PRODUCT',
   PORTFOLIO: 'PORTFOLIO',
   INVESTMENT: 'INVESTMENT',
   SUBGROUP: 'SUBGROUP',
   BRANCH_OFFICE: 'BRANCH_OFFICE',
} as const;
export type OrganisationTypeEnum = (typeof OrganisationType)[keyof typeof OrganisationType];

export const OrganisationsView = {
   OrganisationList: 'table',
   OrganisationHierarchyTree: 'tree',
   OrganisationChart: 'chart',
} as const;
export type OrganisationsViewEnum = (typeof OrganisationsView)[keyof typeof OrganisationsView];

export const EntityTreeFilter = {
   DEFAULT: 'default',
   SUBSIDIARY: 'subsidiary',
   NON_SUBSIDIARY: 'non-subsidiary',
   NON_CONSOLIDATED: 'non-consolidated',
   WITH_CODE: 'with-code',
   DATA_ENTRY: 'data-entry',
} as const;
export type EntityTreeFilterEnum = (typeof EntityTreeFilter)[keyof typeof EntityTreeFilter];

export const PeriodType = {
   INPUT: 'INPUT',
   AGGREGATION: 'AGGREGATION',
} as const;
export type PeriodTypeEnum = (typeof PeriodType)[keyof typeof PeriodType];

export const ReferencedArea = {
   GEN: 'GEN',
   KPI: 'KPI',
   ORG: 'ORG',
   SET: 'SET',
   REP: 'REP',
   EMI: 'EMI',
   EUT: 'EUT',
   RED: 'RED',
} as const;
export type ReferencedAreaEnum = (typeof ReferencedArea)[keyof typeof ReferencedArea];

export const ReferencePersonModel = {
   ASSIGNEE: 'Assignee',
   REVIEWER: 'Reviewer',
} as const;
export type ReferencePersonModelEnum = (typeof ReferencePersonModel)[keyof typeof ReferencePersonModel];

export const ReportType = {
   REGULAR: 'regular',
   AUDIT: 'audit',
   AUDIT_LITE: 'audit-lite',
   MAPPED: 'mapped',
   // emission report types
   CUSTOM: 'custom',
   GHG: 'ghg',
} as const;
export type ReportTypeEnum = (typeof ReportType)[keyof typeof ReportType];
export type SearchResultKindEnum = (typeof SearchResultKind)[keyof typeof SearchResultKind];

export const SearchResultKind = {
   KPI_AREA: 'kpiarea',
   KPI: 'kpi',
   KPI_SUB: 'kpisub',
   KPI_CONTENT: 'kpicontent',
   KPI_FIELD: 'kpifield',
   BUSINESS_ACTIVITY: 'businessactivity',
   TAXONOMY_ACTIVITY: 'taxonomyactivity',
   EMISSION_CATEGORY: 'emissioncategory',
   EMISSION_FACTOR: 'emissionfactor',
   ORGANISATION: 'organisation',
   CHART: 'chart',
   REPORT: 'report',
   REPORT_DOCUMENT: 'reportdocument',
   REPORTING_STANDARD: 'reportingstandard',
   TASK: 'task',
   USER: 'user',
   GROUP: 'group',
   CURRENCY: 'currency',
   CURRENCYRATE: 'currencyrate',
   FOOTPRINT: 'footprint',
   FOOTPRINT_SUB: 'footprintsub',
   FOOTPRINT_CALCULATION: 'footprintcalculation',
   FP_CALCULATION: 'fpcalculation',
   FOOTPRINT_CALCULATION_FIELD: 'footprintcalculationfield',
   FP_CALCULATION_FIELD: 'fpcalculationfield',
   ATTACHMENT: 'attachment',
   TAXONOMYCRITERIARESPONSE: 'taxonomycriteriaresponse',
   TAXONOMYRESPONSEATTACHMENT: 'taxonomyresponseattachment',
   SEARCH_HISTORY: 'searchHistory',
   REDUCTION_PLAN: 'reductionplan',
   REDUCTION_PLAN_TARGET: 'reductionplantarget',
} as const;

export const StatusVerificationTypes = ['NO_VERIFICATION_NEEDED', 'IN_PROGRESS', 'REVIEWED', 'APPROVED', 'ARCHIVED'] as const;
export type StatusVerificationTypeEnum = (typeof StatusVerificationTypes)[number];

export type StatusVerificationEnumExtended = StatusVerificationTypeEnum | 'UNARCHIVE' | 'UNARCHIVED';

export const TaskActionType = {
   TASK: 'TASK',
   ACTION: 'ACTION',
   SUPPLIER_ACTION: 'SUPPLIER_ACTION',
} as const;
export type TaskActionTypeEnum = (typeof TaskActionType)[keyof typeof TaskActionType];

export const TaskRequestType = {
   FUTURE: 'FUTURE',
   ALL: 'ALL',
};
export type TaskRequestTypeEnum = (typeof TaskRequestType)[keyof typeof TaskRequestType];

export const TaskCycle = {
   ONCE: 'once',
   DAILY: 'days',
   WEEKLY: 'weeks',
   MONTHLY: 'months',
   QUARTERLY: 'quarters',
   YEARLY: 'years',
} as const;
export type TaskCycleEnum = (typeof TaskCycle)[keyof typeof TaskCycle];

export const TaskFormMode = {
   CREATE: 'CREATE',
   UPDATE: 'UPDATE',
} as const;
export type TaskFormModeEnum = (typeof TaskFormMode)[keyof typeof TaskFormMode];

export const TaskReferenceKind = {
   KPI_AREA: 'kpiarea',
   KPI: 'kpi',
   KPI_SUB: 'kpisub',
   KPI_FIELD: 'kpifield',
   BUSINESS_ACTIVITY: 'businessactivity',
   REPORT: 'report',
   FOOTPRINT: 'footprint',
   FOOTPRINT_CALCULATION_FIELD: 'footprintcalculationfield',
   REDUCTION_PLAN: 'reductionplan',
   REDUCTION_PLAN_TARGET: 'reductionplantarget',
} as const;

export const TaskStatus = {
   OPEN: 'OPEN',
   PROGRESS: 'PROGRESS',
   IN_PROGRESS: 'IN_PROGRESS',
   HOLD: 'HOLD',
   REVIEW: 'REVIEW',
   IN_REVIEW: 'IN_REVIEW',
   FINISHED: 'FINISHED',
   DONE: 'DONE',
   DRAFT: 'DRAFT',
   DELETED: 'DELETED',
   ARCHIVED: 'ARCHIVED',
   SKIPPED: 'SKIPPED',
   // DRAFT: 'DRAFT',
} as const;
export type TaskStatusEnum = (typeof TaskStatus)[keyof typeof TaskStatus];

export const TaxonomyActivityFilterType = {
   ALL: 'ALL',
   SUGGESTED: 'SUGGESTED',
} as const;

export const TaxonomyAlignmentType = {
   ALIGNED: 'AL',
   ELIGIBLE: 'EL',
   NOT_ELIGIBLE: 'NE',
   NOT_ALIGNED: 'NA',
   OPEN: 'OP',
} as const;

export const TaxonomyContributionType = {
   NOT_QUALIFIED: 'NQ',
   MINIMUM_SAFEGUARDS: 'MS',
   SUBSTANTIAL_CONTRIBUTION: 'SC',
   DO_NO_SIGNIFICANT_HARM: 'DNSH',
   ENABLING_ACTIVITY: 'EA',
   TRANSITIONAL_ACTIVITY: 'TA',
} as const;

export type TaxonomyContributionTypeEnum = (typeof TaxonomyContributionType)[keyof typeof TaxonomyContributionType];
export type TaxonomyAlignmentTypeEnum = (typeof TaxonomyAlignmentType)[keyof typeof TaxonomyAlignmentType];

export const Tolerances = {
   NONE: 'NONE',
   PERCENTAGE: 'PERCENTAGE',
   ABSOLUTE: 'ABSOLUTE',
} as const;

// ------------------ imported from Backend ------------------
// NOTE: cleanup WIP

export const chartEnum = [
   'BAR_CHART',
   'AREA_CHART',
   'COMPOSED_CHART',
   'PIE_CHART',
   'RADAR_CHART',
   'RADIAL_BAR_CHART',
   'SCATTER_CHART',
   'FUNNEL_CHART',
   'LINE_CHART',
   'PERCENT',
   'NUMBER',
   'TABLE',
] as const;
export const characteristicTypeEnum = ['TAXONOMY'];

export const ConsolidationFunction = {
   SUM: 'sum',
   AVERAGE: 'avg',
   MINIMUM: 'min',
   MAXIMUM: 'max',
   WEIGHTED_AVERAGE: 'wavg',
   PERCENTAGE: 'pct',
   NONE: 'none',
} as const;

export type ConsolidationFunctionEnum = (typeof ConsolidationFunction)[keyof typeof ConsolidationFunction] | 'relation';

export const AggregationFunction = {
   SUM: 'sum',
   AVERAGE: 'avg',
   MINIMUM: 'min',
   MAXIMUM: 'max',
   PERCENTAGE: 'pct',
} as const;

export type AggregationFunctionEnum = (typeof AggregationFunction)[keyof typeof AggregationFunction] | 'sum_row' | 'sum_row_col' | 'relation';

export const SectionTemplate = {
   YEAR: 'YEAR',
   HALF_YEAR: 'HALF_YEAR',
   QUARTER: 'QUARTER',
   MONTH: 'MONTH',
   YEAR_YTD: 'YEAR_YTD',
   HALF_YEAR_YTD: 'HALF_YEAR_YTD',
   QUARTER_YTD: 'QUARTER_YTD',
   MONTH_YTD: 'MONTH_YTD',
};

export type SectionTemplateEnum = (typeof SectionTemplate)[keyof typeof SectionTemplate];
export const reportDocumentTemplateUploadPlaceEnum = ['SHAREPOINT', 'S3'];
export const tokenTypeEnum = ['RESETPASSWORD', 'VERIFYEMAIL'];
export type TokenType = (typeof tokenTypeEnum)[number];
export const userSalutationEnum = ['MALE', 'FEMALE', 'DIFFERENT IDENTITY'] as const;
export const userStatusEnum = ['ACTIVE', 'INACTIVE'] as const;
export type UserStatusType = (typeof userStatusEnum)[number];
export const userEmploymentTypeEnum = ['INTERNAL', 'EXTERNAL'];
export const footprintTypeEnum = ['PRODUCT', 'ORGANISATION'] as const;
export const linkTypeEnum = ['LINK', 'SUGGESTION'];
export const productDeliveryEnum = ['AIR', 'SHIP', 'TRAIN', 'ROAD'];

export const valenceEnum = ['POSITIVE', 'NEGATIVE', 'RISK', 'OPPORTUNITY'];

export const importRunJobStatus = ['PENDING', 'SUCCESS', 'WARNING', 'ERROR', 'DELETED'];
export const comparatorValidationRule = ['==', '>=', '<=', '>', '<', '!=', '!!'] as const;
export const specialValidationRuleTypes = ['SWITCH', 'TEXT', 'TEXTAREA'];
export const toleranceValidationRule = ['NONE', 'PERCENTAGE', 'ABSOLUTE'];
export const aggregationFunctionEnum = ['sum', 'avg', 'min', 'max', 'size', 'sum_row', 'sum_row_col', 'relation'];
export const ValueItemMeasurement = ['CHANGE', 'SNAPSHOT', 'YTD'];

export const climateRiskCategoryEnum = ['TEMP', 'WIND', 'WATER', 'SOLIDS', 'OTHER'];
export const climateRiskTypeEnum = ['CHRONIC', 'ACUTE', 'OTHER'];
export const climateRiskActionScenarioTypeEnum = ['RCP', 'SSP', 'SRES'];
export type climateRiskActionScenarioType = (typeof climateRiskActionScenarioTypeEnum)[number];

export const climateRiskActionMitigationEnum = ['COMPLETE', 'PARTIAL', 'NO'];
export const climateRiskActionAnalysisLocationRadiusEnum = ['30m2', '100m2', '1km2', '5km2', '10km2', '50km2', '100km2'];
export const climateRiskActionAnalysisRiskEnum = ['RED_FLAG', 'HIGH', 'MEDIUM', 'LOW', 'NO', 'UNKNOWN'];
export const climateRiskActionAnalysisStatusEnum = ['OPEN', 'RISKS_REQUESTED', 'RISKS_RECEIVED', 'INREVIEW', 'RELEASED'];
export const climateRiskActionAnalysisPeriodEnum = ['ACTUAL', 'FUTURE', 'CONTINUOUSLY'];

export const JobStatusEnum = Object.freeze({
   PENDING: 'PENDING',
   SUCCESS: 'SUCCESS',
   ERROR: 'ERROR',
   DELETED: 'DELETED',
   CANCELLED: 'CANCELLED',
});

export const ExportStatusEnum = Object.freeze({
   PENDING: 'PENDING',
   SUCCESS: 'SUCCESS',
   ERROR: 'ERROR',
   WARNING: 'WARNING',
   CANCELLED: 'CANCELLED',
});

export const businessActivityTypeEnum = Object.freeze(['ALL', ...Object.values(FSLIType)]);

export type BusinessActivityType = (typeof businessActivityTypeEnum)[number];
export const StrategyPriority = {
   1: 'LOW',
   2: 'MEDIUM',
   3: 'HIGH',
} as const;
export type StrategyPriorityTypeEnum = (typeof StrategyPriority)[keyof typeof StrategyPriority];

export const generalRiskSourceValues = ['ISO', 'CUSTOM', 'BSI', 'SUPPLIER', 'LAW', 'CONTRACT', 'SELF_DISCOVERY'] as const;
export type GeneralRiskSource = (typeof generalRiskSourceValues)[number];
export const generalRiskRecommendationValues = ['ACCEPT', 'MITIGATE', 'TRANSFER', 'AVOID', 'MONITOR', 'OTHER'] as const;
export const generalRiskStepVariants = [
   'risk',
   'weakpoint',
   'mitigation',
   'recommendation',
   'assessment',
   'affectedItem',
   'threat',
   'possibleConsequences',
] as const;
export type GeneralRiskStepVariantType = (typeof generalRiskStepVariants)[number];
export const generalRiskPersonTypes = ['reviewer', 'approver', 'assignee'] as const;
export type GeneralRiskPersonType = (typeof generalRiskPersonTypes)[number];
export const riskAffectedItemVariants = ['ASSET', 'AREA', 'PROCESS'] as const;
export type RiskAffectedItemVariant = (typeof riskAffectedItemVariants)[number];
export const riskModuleStatusEnum = ['OPEN', 'PROGRESS', 'HOLD', 'CLOSED', 'FINISHED'] as const;
export type RiskModuleStatus = (typeof riskModuleStatusEnum)[number];

export const ExportKindEnum = Object.freeze({
   EMISSION: 'emission',
   KPI: 'kpi',
   KPI_AUDIT: 'kpi-audit',
   KPI_AUDIT_LITE: 'kpi-audit-lite',
});

export const TagContextEnum = ['ENTITY', 'DOCUMENT_HUB', 'STRATEGY', 'SUPPLY_CHAIN'] as const;

export const AttachmentOriginEnum = ['DOCUMENT_HUB', 'SUPPLY_CHAIN'] as const;

export type TagContextType = (typeof TagContextEnum)[number];

// Tolerates the single value the API returned before contexts became a list.
export const filterTagsByContext = <T extends { context?: unknown }>(tags: readonly T[] | null | undefined, context: TagContextType): T[] =>
   (tags ?? []).filter((tag) => normalizeTagContexts(tag.context).includes(context));

export const normalizeTagContexts = (value: unknown): TagContextType[] => {
   const entries = Array.isArray(value) ? value : [value];

   return entries.filter((entry): entry is TagContextType => typeof entry === 'string' && (TagContextEnum as readonly string[]).includes(entry));
};
export type AttachmentOriginType = (typeof AttachmentOriginEnum)[number];

export const DeleteOperationStatusEnum = Object.freeze({
   PENDING: 'PENDING',
   SUCCESS: 'SUCCESS',
   ERROR: 'ERROR',
});

export type DeleteOperationStatus = (typeof DeleteOperationStatusEnum)[keyof typeof DeleteOperationStatusEnum];

export const TimeSliceValidityStatusEnum = Object.freeze({
   ACTIVE: 'active',
   EXPIRED: 'expired',
   FUTURE: 'future',
});

export type TimeSliceValidityStatus = (typeof TimeSliceValidityStatusEnum)[keyof typeof TimeSliceValidityStatusEnum];

export const lca_activities = Object.freeze([
   'unknown',
   'cradle_to_gate',
   'cradle_to_farm_gate',
   'cradle_to_processing_gate',
   'cradle_to_shelf',
   'cradle_to_grave',
   'gate_to_grave',
   'fuel_combustion',
   'biogenic_co2_combustion',
   'use_phase',
   'electricity_generation',
   'transmission_and_distribution',
   'well_to_tank',
   'well_to_wheel',
   'tank_to_wheel',
   'well_to_propeller',
   'direct',
   'indirect',
   'total',
   'upstream-end_of_life',
   'end_of_life',
   'upstream-use_phase-transport',
   'fugitive_release',
   'fugitive_release_non_kyoto',
   'electricity_generation-transmission_and_distribution',
   'upstream',
   'carbon_storage-cradle_to_gate',
   'upstream-use_phase',
   'upstream-fuel_combustion',
   'upstream-electricity_generation',
   'upstream-electricity_generation-transmission_and_distribution',
   'well_to_tank-transmission_and_distribution',
   'upstream-use_phase-transport-transmission_and_distribution',
   'upstream-transmission_and_distribution',
]);

export const scopes = Object.freeze([
   '1',
   '2',
   '3',
   '3.1',
   '3.2',
   '3.3',
   '3.4',
   '3.5',
   '3.6',
   '3.7',
   '3.8',
   '3.9',
   '3.11',
   '3.12',
   '3.13',
   '3.14',
   '3.15',
]);
